import { useState, useRef } from 'react';

import { useTheme, useMount } from '@simplywallst/ui-core';
import type { Theme } from '@simplywallst/ui-core';

import type { WatchlistItem } from '@/hooks/useWatchlist';

type Args = {
  defaultId: WatchlistItem['id'] | null;
};

function getMediaQuery({ theme }: { theme: Theme }) {
  return typeof window === 'undefined'
    ? null
    : window.matchMedia(`(min-width: ${theme.lgMin})`);
}

export function usePanelWatchlistItemId({ defaultId }: Args) {
  const theme = useTheme();
  const mediaQuery = useRef(getMediaQuery({ theme }));

  const [panelWatchlistItemId, setPanelWatchlistItemId] = useState<
    WatchlistItem['id'] | null
  >(() => {
    if (mediaQuery.current?.matches && defaultId) {
      return defaultId;
    }
    return null;
  });

  useMount(() => {
    if (!mediaQuery.current) {
      mediaQuery.current = getMediaQuery({ theme });
    }

    const syncDefaultWatchlistItemId = (media: { matches: boolean } | null) => {
      if (media?.matches && defaultId && panelWatchlistItemId === null) {
        setPanelWatchlistItemId(defaultId);
      }
    };

    syncDefaultWatchlistItemId(mediaQuery.current);

    mediaQuery.current?.addEventListener('change', syncDefaultWatchlistItemId);
    // cleanup
    return () => {
      mediaQuery.current?.removeEventListener(
        'change',
        syncDefaultWatchlistItemId
      );
    };
  });

  return [panelWatchlistItemId, setPanelWatchlistItemId] as const;
}
